import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ConnectivityService } from '@core/health-check/connectivity.service';
import { SKIP_CONNECTIVITY_TRACKING } from '@core/health-check/connectivity.model';
import { NetworkUnavailableError } from '@core/http/models/network-unavailable.error';

/**
 * Признак «до бекенда не достучались»: сеть отвалилась (status 0) либо шлюз не смог
 * проксировать запрос. 503 с кодом externalApiNotAvailable сюда не относится — это
 * недоступность внешней системы, её обрабатывает HttpResponseCodeHandlerInterceptor.
 */
function isBackendUnreachable(e: unknown): boolean {
  if (!(e instanceof HttpErrorResponse)) {
    return false;
  }

  if (e.status === 503) {
    return e.error?.code !== 'externalApiNotAvailable';
  }

  return e.status === 0 || e.status === 502 || e.status === 504;
}

/**
 * Кормит ConnectivityService сигналами от реальных запросов: успех — связь жива,
 * сетевая ошибка — повод заподозрить обрыв. Сетевые ошибки заодно переводятся в
 * NetworkUnavailableError, чтобы GlobalErrorHandlerService не сыпал тостами
 * «Произошла непредвиденная ошибка» на каждый висящий запрос.
 */
@Injectable()
export class ConnectivityInterceptor implements HttpInterceptor {
  private connectivity = inject(ConnectivityService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.context.get(SKIP_CONNECTIVITY_TRACKING)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.connectivity.reportSuccess();
          }
        },
      }),
      catchError((e) => {
        if (isBackendUnreachable(e)) {
          this.connectivity.reportNetworkFailure();
          return throwError(() => new NetworkUnavailableError());
        }

        return throwError(() => e);
      }),
    );
  }
}
